import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Card, Icon, Image, Divider, Statistic, List, Loader, Label } from 'semantic-ui-react';
import { DyzioPrimaryButton } from 'dyzio-shared-components/lib/components/dyzio-buttons';
import { compose } from 'recompose';
import { withRouter } from 'react-router';
import TargetLink from '../target-link';
import ThemeSelector from '../../themes/theme-selector';

const { isFullWhiteLabel } = ThemeSelector.theme();
const DYZIO_CAMPAINGS_LINK = 'http://help.dyzio.co/campaigns/a-guide-to-dyzio-campaigns';
const KEY_FEATURES_LINK = 'http://help.dyzio.co/dyzio-key-features/dyzio-key-features';
const INFLUENCER_DB_LINK = 'http://help.dyzio.co/my-influencers-database/your-private-influencer-database';


const centeredText = {
  textAlign: 'center',
};

const loadingContentStyle = {
  height: 105,
  paddingTop: 34,
};

const DashboardCard = ({
  image,
  color,
  value,
  label,
  onButtonClick,
  buttonText,
  buttonIcon,
  isLoading,
  onCardClick,
  subtext,
}) => {
  const handleButtonClick = event => {
    event.stopPropagation();
    onButtonClick();
  };
  const subtextStyle = {
    fontSize: '80%',
    opacity: '0.6',
  };
  return (
    <Card color={color} onClick={onCardClick} style={centeredText} raised>
      <Card.Content>
        { image }
      </Card.Content>
      {
        isLoading &&
        <Card.Content style={loadingContentStyle}>
          <Loader active inline="centered" />
        </Card.Content>
      }
      {
        !isLoading &&
        <Card.Content>
          <Statistic>
            <Statistic.Value>{ value }</Statistic.Value>
            <Statistic.Label>{ label }</Statistic.Label>
            {
              subtext &&
              <Statistic.Label style={subtextStyle}>
                { subtext }
              </Statistic.Label>
            }
          </Statistic>
        </Card.Content>
      }
      <Card.Content>
        <DyzioPrimaryButton
          onClick={handleButtonClick}
          color={color}
          hasIcon
          disabled={isLoading}
          icon={buttonIcon}
          label={buttonText}
          floated={null}
        />
      </Card.Content>
    </Card>
  );
};

const GettingStartingCard = () => (
  <Card>
    <Card.Content>
      <h4>Getting started with dyzio?</h4>
      <Divider />
      <Divider hidden />
      <List>
        <List.Item>
          <TargetLink renderIf href={KEY_FEATURES_LINK}>Dyzio Key features</TargetLink>
        </List.Item>
        <List.Item>
          <TargetLink renderIf href={INFLUENCER_DB_LINK}>Your private influencer database</TargetLink>
        </List.Item>
        <List.Item>
          <TargetLink renderIf href={DYZIO_CAMPAINGS_LINK}>A guide to Dyzio campaigns</TargetLink>
        </List.Item>
      </List>
    </Card.Content>
  </Card>
);

DashboardCard.propTypes = {
  color: PropTypes.string.isRequired,
  image: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  subtext: PropTypes.string,
  onButtonClick: PropTypes.func.isRequired,
  onCardClick: PropTypes.func.isRequired,
  buttonText: PropTypes.string.isRequired,
  buttonIcon: PropTypes.node.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

DashboardCard.defaultProps = {
  subtext: null,
};

const getCards = props => {
  const {
    router,
    onToggleAddCampaignModalState,
    totals, visibleCampaigns,
    isLoadingTotals,
    organisation,
  } = props;
  const {
    licenseTrackedAssetLimit,
    licenseInfluencerLimit,
  } = organisation || {};
  return [
    {
      image: <Image src="/icons/my-influencers.svg" size="tiny" className="cta" />,
      color: 'pink',
      value: _.get(totals, 'totalInfluencers', '-'),
      label: 'influencers',
      onButtonClick: () => router.push('/influencer/add'),
      onCardClick: () => router.push('/influencers'),
      subtext: `Limit: ${isFullWhiteLabel ? 'Unlimited' : licenseInfluencerLimit}`,
      buttonText: 'Add an influencer',
      buttonIcon: <Icon name="add" />,
      isLoading: isLoadingTotals || organisation === null,
    },
    {
      image: <Image src="/icons/campaign-management.svg" size="tiny" className="cta" />,
      color: 'green',
      value: _.get(totals, 'totalCampaigns', '-'),
      subtext: `You can see ${visibleCampaigns || '-'}`,
      label: 'campaigns',
      onButtonClick: onToggleAddCampaignModalState,
      onCardClick: () => router.push('/dashboard'),
      buttonText: 'Add a Campaign',
      buttonIcon: <Icon name="add" />,
      isLoading: isLoadingTotals,
    },
    {
      image: <Image src="/icons/live-analytics.svg" size="tiny" className="cta" />,
      color: 'orange',
      label: 'tracked assets',
      value: _.get(totals, 'trackedAssetsCount', '-'),
      subtext: `Limit: ${isFullWhiteLabel ? 'Unlimited' : licenseTrackedAssetLimit}`,
      onButtonClick: () => router.push('/dashboard'),
      onCardClick: () => router.push('/dashboard'),
      buttonText: 'View campaigns',
      buttonIcon: <Icon name="add" />,
      isLoading: isLoadingTotals || organisation === null,
    },
    {
      image: <Image src="/icons/users.svg" size="tiny" bordered shape="circular" className="cta" />,
      color: 'blue',
      value: _.get(totals, 'totalTeamMembers', '-'),
      label: 'team members',
      subtext: 'Limit: Unlimited',
      onButtonClick: () => router.push('/team/manage'),
      onCardClick: () => router.push('/team/manage'),
      buttonText: 'Add a team member',
      buttonIcon: <Icon name="add" />,
      isLoading: isLoadingTotals,
    },
  ];
};


const DashboardCardGroup = props => {
  const { disableHelpLinks } = ThemeSelector.theme();
  const { organisation } = props;
  return (
    <div>
      <Divider hidden />
      <h2>
        Organisation Overview
        {' '}
        {!isFullWhiteLabel && organisation && <Label>
          {`${organisation.licenseSubscriptionType} PLAN`}
        </Label>}
      </h2>
      <Divider hidden />
      <Card.Group itemsPerRow={disableHelpLinks ? 4 : 4}>
        { _.map(getCards(props), cardProps => <DashboardCard {...cardProps} />) }
      </Card.Group>
    </div>
  );
};

DashboardCardGroup.propTypes = {
  organisation: PropTypes.object,
};

DashboardCardGroup.defaultProps = {
  organisation: null,
};

export default compose(withRouter)(DashboardCardGroup);



/** WEBPACK FOOTER **
 ** ./src/components/dashboard-card-group/index.js
 **/